import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Checkbox,
  Button,
  Spin,
  Row,
  Col,
  message,
  Tag,
  Tooltip,
  Divider,
  Alert,
  Space,
} from "antd";
import {
  SafetyCertificateOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import API from "../Utils/API";

const RESOURCE_LABELS = {
  leads: "Leads",
  clients: "Clients",
  clients_developer: "Developers",
  properties_bunglow: "Properties: Bunglow",
  properties_flat: "Properties: Flat",
  properties_rent_residential: "Properties: Rent Residential",
  properties_commercial: "Properties: Commercial",
  properties_rent_commercial: "Properties: Rent Commercial",
  properties_land: "Properties: Land",
  properties_na_agriculture: "Properties: NA / Agriculture",
  properties_bunglow_developer: "Builder: Bunglow",
  properties_flat_developer: "Builder: Flat",
  properties_commercial_developer: "Builder: Commercial",
  properties_land_developer: "Builder: Land",
  projects_residential: "Projects: Residential",
  projects_commercial: "Projects: Commercial",
  projects_land: "Projects: Land",
  areas: "Areas",
};

const ACTIONS = [
  { key: "index", label: "List" },
  { key: "show", label: "View" },
  { key: "create", label: "Create" },
  { key: "update", label: "Edit" },
  { key: "destroy", label: "Delete" },
];

export const ManagePermissionsModal = ({
  visible,
  onClose,
  userId,
  userName,
  userEmail,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedSet, setSelectedSet] = useState(new Set());

  const isActionSupported = (resource, action) => {
    if (resource === "areas" && (action === "index" || action === "show")) {
      return false;
    }
    if (
      (resource === "projects_residential" ||
        resource === "projects_commercial" ||
        resource === "projects_land") &&
      (action === "index" || action === "show")
    ) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (visible && userId) {
      fetchPermissions();
    }
  }, [visible, userId]);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await API.get(`/users/${userId}/permissions`);
      const permissions = response.data?.permissions || [];
      const newSet = new Set();
      permissions.forEach((p) => {
        if (p.resource && p.action) {
          newSet.add(`${p.resource}:${p.action}`);
        }
      });
      setSelectedSet(newSet);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      message.error("Failed to load user permissions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (resource, action, checked) => {
    const newSet = new Set(selectedSet);
    const key = `${resource}:${action}`;
    if (checked) {
      newSet.add(key);

      // Auto-enable clients:index and areas:index if enabling leads or properties
      if (resource === "leads" || resource.startsWith("properties_")) {
        if (resource.endsWith("_developer")) {
          newSet.add("clients_developer:index");
        } else {
          newSet.add("clients:index");
        }
        newSet.add("areas:index");
      }
    } else {
      newSet.delete(key);
    }
    setSelectedSet(newSet);
  };

  const handleToggleRow = (resource, makeChecked) => {
    const newSet = new Set(selectedSet);
    ACTIONS.forEach((act) => {
      if (isActionSupported(resource, act.key)) {
        const key = `${resource}:${act.key}`;
        if (makeChecked) {
          newSet.add(key);
        } else {
          newSet.delete(key);
        }
      }
    });

    if (makeChecked) {
      if (resource === "leads" || resource.startsWith("properties_")) {
        if (resource.endsWith("_developer")) {
          newSet.add("clients_developer:index");
        } else {
          newSet.add("clients:index");
        }
        newSet.add("areas:index");
      }
    }
    setSelectedSet(newSet);
  };

  const handleToggleAll = (makeChecked) => {
    const newSet = new Set(selectedSet);
    Object.keys(RESOURCE_LABELS).forEach((res) => {
      ACTIONS.forEach((act) => {
        if (isActionSupported(res, act.key)) {
          const key = `${res}:${act.key}`;
          if (makeChecked) {
            newSet.add(key);
          } else {
            newSet.delete(key);
          }
        }
      });
    });
    setSelectedSet(newSet);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const permissions = Array.from(selectedSet).map((item) => {
        const [resource, action] = item.split(":");
        return { resource, action };
      });

      await API.put(`/users/${userId}/permissions`, { permissions });
      message.success("Permissions updated successfully!");
      onClose();
    } catch (error) {
      console.error("Error saving permissions:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        message.error("Unauthorized: Only Admins can modify permissions.");
      } else {
        message.error(
          error.response?.data?.message || "Failed to save permissions.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const tableData = Object.keys(RESOURCE_LABELS).map((resource) => ({
    key: resource,
    resource,
    resourceLabel: RESOURCE_LABELS[resource],
  }));

  const columns = [
    {
      title: "Resource / Feature",
      dataIndex: "resourceLabel",
      key: "resource",
      width: 220,
      render: (text) => (
        <span style={{ fontWeight: "600", color: "#2c3e50" }}>{text}</span>
      ),
    },
    ...ACTIONS.map((act) => ({
      title: act.label,
      key: act.key,
      align: "center",
      width: 100,
      render: (_, record) => {
        const supported = isActionSupported(record.resource, act.key);
        if (!supported) {
          return (
            <Tooltip title="Not applicable for this resource">
              <span
                style={{
                  color: "#d1d5db",
                  fontSize: "12px",
                  cursor: "not-allowed",
                }}
              >
                —
              </span>
            </Tooltip>
          );
        }

        const isChecked = selectedSet.has(`${record.resource}:${act.key}`);
        return (
          <Checkbox
            checked={isChecked}
            onChange={(e) =>
              handleToggle(record.resource, act.key, e.target.checked)
            }
          />
        );
      },
    })),
    {
      title: "Row Actions",
      key: "row_actions",
      align: "center",
      width: 120,
      render: (_, record) => {
        const supportedActions = ACTIONS.filter((act) =>
          isActionSupported(record.resource, act.key),
        );
        const allChecked = supportedActions.every((act) =>
          selectedSet.has(`${record.resource}:${act.key}`),
        );

        return (
          <Space size="small">
            <Button
              type="text"
              size="small"
              style={{ color: "#1890ff", fontSize: "12px" }}
              onClick={() => handleToggleRow(record.resource, true)}
            >
              All
            </Button>
            <Divider type="vertical" />
            <Button
              type="text"
              size="small"
              danger
              style={{ fontSize: "12px" }}
              onClick={() => handleToggleRow(record.resource, false)}
            >
              Clear
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SafetyCertificateOutlined
            style={{ color: "#1890ff", fontSize: "20px" }}
          />
          <span>Manage Employee Permissions</span>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      width={850}
      destroyOnClose
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={saving}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={saving}
          disabled={loading}
        >
          Save Changes
        </Button>,
      ]}
    >
      <div style={{ margin: "16px 0" }}>
        <Alert
          message={
            <div>
              Granting granular permissions gives{" "}
              <strong>{userName || "Employee"}</strong> ({userEmail}) access to
              specific backend resources. Actions not supported by a resource
              are disabled automatically.
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Tag color="blue" style={{ fontSize: "13px", padding: "4px 8px" }}>
              Employee ID: {userId}
            </Tag>
          </Col>
          <Col>
            <Space>
              <Button
                size="small"
                onClick={() => handleToggleAll(true)}
                icon={<CheckOutlined />}
              >
                Grant All Supported
              </Button>
              <Button
                size="small"
                danger
                onClick={() => handleToggleAll(false)}
                icon={<CloseOutlined />}
              >
                Revoke All
              </Button>
            </Space>
          </Col>
        </Row>

        <Spin spinning={loading} tip="Fetching permissions...">
          <Table
            dataSource={tableData}
            columns={columns}
            pagination={false}
            bordered
            size="small"
            style={{ borderRadius: "8px", overflow: "hidden" }}
          />
        </Spin>
      </div>
    </Modal>
  );
};
